import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import FormLayout from '../components/FormLayout';
import FormHeader from '../components/FormHeader';
import Form from '../components/Form';
import Input from '../components/Input';
import Button from '../components/Button';
import Alert from '../components/Alert';

type View = 'loading' | 'confirm' | 'email' | 'success' | 'error';

const SETTINGS_PATH = '/settings/notifications';
const SETTINGS_LOGIN = `/login?next=${encodeURIComponent(SETTINGS_PATH)}`;

const ManageSettingsLink: React.FC<{ to: string; children: React.ReactNode }> = ({
  to,
  children,
}) => (
  <Link
    to={to}
    className="text-emerald-500 hover:text-emerald-400 font-semibold underline underline-offset-2 transition-colors duration-200"
  >
    {children}
  </Link>
);

const UnsubscribePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('t') || '').trim();
  const { firebaseUser } = useAuth();
  const settingsHref = firebaseUser ? SETTINGS_PATH : SETTINGS_LOGIN;

  const [view, setView] = useState<View>(token ? 'loading' : 'email');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setView('email');
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const getStatus = httpsCallable(functions, 'getEmailUnsubscribeStatus');
        const result = await getStatus({ token });
        const data = result.data as { valid?: boolean; alreadyUnsubscribed?: boolean };
        if (cancelled) return;
        if (!data.valid) {
          setView('email');
          return;
        }
        if (data.alreadyUnsubscribed) {
          setView('success');
          return;
        }
        setView('confirm');
      } catch {
        if (!cancelled) setView('email');
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const confirmWithToken = async () => {
    setSubmitting(true);
    setMessage('');
    try {
      const confirm = httpsCallable(functions, 'confirmEmailUnsubscribe');
      await confirm({ token });
      setView('success');
    } catch (err: unknown) {
      setView('error');
      setMessage((err as Error)?.message || 'Could not unsubscribe. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmWithEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');
    try {
      const confirm = httpsCallable(functions, 'confirmEmailUnsubscribe');
      await confirm({ email });
      setView('success');
    } catch (err: unknown) {
      setView('error');
      setMessage((err as Error)?.message || 'Could not unsubscribe. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (view === 'loading') {
    return (
      <FormLayout>
        <FormHeader title="Unsubscribe" subtitle="Checking your link…" />
      </FormLayout>
    );
  }

  if (view === 'success') {
    return (
      <FormLayout>
        <FormHeader
          title="You’ve been unsubscribed"
          subtitle="You won’t receive PRAIRI digest or marketing emails anymore. Essential emails like password reset and order confirmation will still be sent."
        />
        <p className="text-gray-400 text-sm text-center">
          Want to change other notifications?{' '}
          <ManageSettingsLink to={settingsHref}>Manage notification settings</ManageSettingsLink>
        </p>
      </FormLayout>
    );
  }

  if (view === 'confirm') {
    return (
      <FormLayout>
        <FormHeader
          title="Unsubscribe from PRAIRI emails?"
          subtitle="You’ll stop getting digest and marketing emails. You can still receive essential messages like password reset and order confirmation."
        />
        <Button onClick={confirmWithToken} disabled={submitting}>
          {submitting ? 'Unsubscribing…' : 'Unsubscribe'}
        </Button>
        {message && <Alert type="error">{message}</Alert>}
        <p className="mt-6 text-gray-400 text-sm text-center">
          Or{' '}
          <ManageSettingsLink to={settingsHref}>
            manage all notification settings
          </ManageSettingsLink>
        </p>
      </FormLayout>
    );
  }

  return (
    <FormLayout>
      <FormHeader
        title="Unsubscribe from PRAIRI emails"
        subtitle="Enter the email address that received the message. You’ll stop digest and marketing emails only."
      />
      <Form onSubmit={confirmWithEmail}>
        <Input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          placeholder="you@example.com"
          label="Email address"
          disabled={submitting}
        />
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Unsubscribing…' : 'Unsubscribe'}
        </Button>
      </Form>
      {view === 'error' && message && <Alert type="error">{message}</Alert>}
      <p className="mt-6 text-gray-400 text-sm text-center">
        Or{' '}
        <ManageSettingsLink to={settingsHref}>manage all notification settings</ManageSettingsLink>
      </p>
    </FormLayout>
  );
};

export default UnsubscribePage;
